import { Dropdown, Option } from '@fluentui/react-components';
import {
  LessonThemeResponse,
  MethodOfTeachingResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { find, orderBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { CommunicationMessage, Illustrations, IllustrationSize } from '../../../Communication';
import { Loader } from '../../../Loader/Loader';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';
import { Textarea } from '../../../Textarea';
import './LessonContentDialog.scss';

export interface LessonContentDialogProps {
  open: boolean;
  allMethodsOfTeaching: MethodOfTeachingResponse[];
  lessonThemeResponse: LessonThemeResponse;
  isLoading?: boolean;
  isUpdating?: boolean;
  onCancelClick: () => void;
  onSaveClick(content: LessonThemeResponse): void;
}

export const LessonContentDialog: React.FC<LessonContentDialogProps> = ({
  lessonThemeResponse,
  open,
  onCancelClick,
  onSaveClick,
  allMethodsOfTeaching,
  isLoading,
  isUpdating,
}) => {
  const { t } = useTranslation();
  const [lessonContent, setLessonContent] = useState<LessonThemeResponse>(lessonThemeResponse);

  const updateLessonContent = (updated: Partial<LessonThemeResponse>) =>
    setLessonContent({ ...lessonContent, ...updated });

  const sortedMethodsOfTeaching = useMemo(
    () => orderBy(allMethodsOfTeaching, [(method) => method.name?.toLocaleLowerCase()]),
    [allMethodsOfTeaching],
  );

  const selectedMethodOfTeaching = lessonContent?.method;

  const header =
    lessonContent.id === undefined
      ? t('classbook:lessoncontentdialog:edit', { defaultValue: 'Stundeninhalt bearbeiten' })
      : t('classbook:lessoncontentdialog:new', { defaultValue: 'Neuer Stundeninhalt' });

  const isFormValid = useMemo(
    () =>
      lessonContent.method !== undefined &&
      lessonContent.method !== null &&
      lessonContent.themeDescription !== undefined &&
      lessonContent.themeDescription !== null &&
      lessonContent.themeDescription.length > 0,
    [lessonContent],
  );

  return (
    <ResponsiveDialog
      open={open}
      header={header}
      primaryAction={
        <Button
          appearance="primary"
          loading={isUpdating}
          disabled={!isFormValid || isUpdating}
          onClick={() => onSaveClick(lessonContent)}
        >
          {t('save', { defaultValue: 'Speichern' })}
        </Button>
      }
      secondaryAction={
        <Button
          aria-label={t('cancel_label', {
            defaultValue: 'Schließt den Dialog und öffnet vorherige Seite.',
          })}
          disabled={isUpdating}
          onClick={onCancelClick}
        >
          {t('cancel', { defaultValue: 'Abbrechen' })}
        </Button>
      }
    >
      {isLoading && <Loader />}

      {!isLoading && (
        <>
          {allMethodsOfTeaching.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-col">
                <span>
                  {t('classbook:lessoncontentdialog:lesson_form', {
                    defaultValue: 'Unterrichtsform',
                  })}
                </span>
                <Dropdown
                  appearance="filled-darker"
                  disabled={isUpdating}
                  value={selectedMethodOfTeaching?.name ?? ''}
                  selectedOptions={
                    selectedMethodOfTeaching?.id ? [selectedMethodOfTeaching.id] : []
                  }
                  onOptionSelect={(_, data) => {
                    const methodOfTeaching = find(allMethodsOfTeaching, {
                      id: data.optionValue,
                    });
                    if (methodOfTeaching) {
                      updateLessonContent({ method: methodOfTeaching });
                    }
                  }}
                >
                  {sortedMethodsOfTeaching.map((method) => (
                    <Option key={method.id} value={method.id} text={method.name ?? ''}>
                      {method.name}
                    </Option>
                  ))}
                </Dropdown>
              </div>
              <Textarea
                appearance="filled-darker"
                disabled={isUpdating}
                className="newLessonContentDialogTextArea"
                resize="vertical"
                placeholder={t('classbook:lessoncontentdialog:type_in_content', {
                  defaultValue: 'Geben Sie hier Ihren Stundeninhalt ein',
                })}
                onChange={(_, data) => {
                  if (!data) {
                    return;
                  }

                  updateLessonContent({ themeDescription: data.value ?? '' });
                }}
                value={lessonContent?.themeDescription ?? ''}
              />
            </div>
          )}
          {allMethodsOfTeaching.length === 0 && (
            <CommunicationMessage
              headline={t('classbook:lessoncontentdialog:no_form_of_teaching', {
                defaultValue: 'Keine Lehrformen konfiguriert',
              })}
              text={t('classbook:lessoncontentdialog:no_form_of_teaching_created', {
                defaultValue:
                  'Es wurden noch keine Lehrformen erstellt. Bitte wenden Sie sich an Ihren Administrator.',
              })}
              illustration={Illustrations.Empty}
              illustrationSize={IllustrationSize.Medium}
            />
          )}
        </>
      )}
    </ResponsiveDialog>
  );
};
